import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Howl } from 'howler';
import keypressSound from '../../assets/typewriter/keypress.mp3';
import returnSound from '../../assets/typewriter/return.mp3';
import './TypewriterPage.css';
import { TypewriterScriptService } from '../../services/typewriterScriptService';
import { useAuth } from '../../hooks/useAuth';
import { markSplashSeen } from '../../hooks/useOnboardingStatus';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';

const BASE_LAYOUT_WIDTH = 1180;
const FINAL_RELEASE_HOLD = 800;
const SPLASH_FADE_DURATION = 900;
const USER_INTERACTION_FADE_DURATION = 5000;
const SNOW_MIN_PARTICLES = 1800;
const SNOW_MAX_PARTICLES = 7200;
const SNOW_AREA_DIVISOR = 160;
const MANUAL_BUFFER_INTERVAL = 20;
const FADE_FLASH_DURATION = 180; // milliseconds for interruption flash

const DEFAULT_AUTO_SCRIPT_LINES: string[] = [
  '',
  'why ionoff.com over chatgpt.com,',
  'gemini.com, or other lone ai chatbots?',
  '',
  'faster to respond',
  '',
  'more robust',
  '',
  'then unbeatable intelligence',
  '(powered by peer review)',
  '',
  'no subscription',
  '(though can help you shop for one)',
  '',
  'leaderboards centered on you',
  '(not benchmark averages)',
  '',
  'situational awareness grounded in',
  'experience with full ai landscape',
  '(not an oligopoly)',
  '',
  "let's go!",
];

const parseScriptToLines = (script: string): string[] => {
  if (!script) {
    return [];
  }
  return script.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
};

// Point/Vector class for position calculations
class Point {
  x: number;
  y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  add(other: Point | number): Point {
    if (typeof other === 'number') {
      return new Point(this.x + other, this.y + other);
    }
    return new Point(this.x + other.x, this.y + other.y);
  }

  subtract(other: Point | number): Point {
    if (typeof other === 'number') {
      return new Point(this.x - other, this.y - other);
    }
    return new Point(this.x - other.x, this.y - other.y);
  }

  divideBy(divisor: number): Point {
    return new Point(this.x / divisor, this.y / divisor);
  }

  multiplyBy(multiplier: number): Point {
    return new Point(this.x * multiplier, this.y * multiplier);
  }
}

// Character class for rendering
class Char {
  x: number;
  y: number;
  s: string;
  rotate: number;
  alpha: number;
  typewriter: Typewriter;
  canvasOffset: Point;
  lineY: number; // Track which line this character is on
  fixedOffsetX: number | null; // If line is completed, this stores its final centered offset

  constructor(typewriter: Typewriter, char: string, x: number, y: number, rotation: number = 0, lineY: number = 0) {
    this.typewriter = typewriter;
    this.s = char;
    this.x = x;
    this.y = y;
    this.rotate = rotation; // Rotation passed from constructor for typewriter jitter
    this.alpha = 0.72 + (Math.random() * 0.12 - 0.06); // Slight random alpha
    this.canvasOffset = new Point(0, 0);
    this.lineY = lineY;
    this.fixedOffsetX = null;
  }

  draw(ctx: CanvasRenderingContext2D) {
    // Use fixed offset for completed lines, current offset for active line
    const offsetX = this.fixedOffsetX !== null ? this.fixedOffsetX : this.typewriter.canvasOffset.x;
    const offsetY = this.typewriter.canvasOffset.y;
    const pos = new Point(this.x + offsetX, this.y + offsetY);
    
    // Draw directly at position - context already has DPR scaling applied
    ctx.save();
    ctx.globalAlpha = 1.0;
    
    const charWidth = this.typewriter.charWidth;
    const charHeight = this.typewriter.charHeight;
    const gap = 2; // Gap between characters
    const horizontalPadding = 6; // Horizontal padding inside black block (wider blocks)
    
    // Calculate black block dimensions (add horizontal padding)
    const blockWidth = charWidth - gap + horizontalPadding;
    const blockHeight = charHeight;
    
    // Calculate center point for rotation
    const centerX = pos.x + blockWidth / 2;
    const centerY = pos.y + blockHeight / 2;
    
    // Apply rotation around center point
    ctx.translate(centerX, centerY);
    ctx.rotate(this.rotate);
    ctx.translate(-centerX, -centerY);
    
    // Draw black background rectangle behind character (like ionoff logo)
    ctx.fillStyle = '#000000';
    ctx.fillRect(pos.x, pos.y, blockWidth, blockHeight);
    
    // Draw gray character centered within the black block (softer contrast)
    ctx.fillStyle = '#b0b0b0'; // Disabled-level gray instead of harsh white
    ctx.font = `${this.typewriter.fontSize}px "Special Elite", "Courier New", monospace`;
    ctx.textBaseline = 'middle'; // Vertical centering
    ctx.textAlign = 'center'; // Horizontal centering
    
    ctx.fillText(this.s, centerX, centerY);
    
    // Reset text alignment for other drawing operations
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.restore();
  }
}

// Cursor class
class Cursor {
  position: Point;
  typewriter: Typewriter;
  inputElement: HTMLInputElement | null;
  charWidth: number;
  charHeight: number;
  cursorTimeout: number | null = null;
  raf: number | null = null;
  opacity: number = 0.72;

  constructor(typewriter: Typewriter, inputElement: HTMLInputElement | null, charWidth: number, charHeight: number) {
    this.typewriter = typewriter;
    this.inputElement = inputElement;
    this.charWidth = charWidth;
    this.charHeight = charHeight;
    // Initialize at typewriter's start position (center)
    this.position = typewriter.startPos;
  }

  updateInputPosition() {
    if (this.inputElement) {
      const maxX = this.typewriter.viewportWidth - this.charWidth;
      const maxY = this.typewriter.viewportHeight;
      const x = Math.min(this.position.x, maxX);
      const y = Math.min(this.position.y, maxY);
      this.inputElement.style.transform = `translate(${x}px, ${y}px)`;
    }
  }

  clear(ctx: CanvasRenderingContext2D) {
    // Cursor box disabled - no need to clear
    // const pos = this.position.subtract(1);
    // ctx.clearRect(pos.x, pos.y, this.charWidth + 4, this.charHeight + 4);
  }

  update(ctx: CanvasRenderingContext2D, newPos: Point) {
    // Cursor box disabled - no drawing needed
    // this.clear(ctx);
    this.position = newPos;
    this.updateInputPosition();
    // this.draw(ctx);
  }

  draw(ctx: CanvasRenderingContext2D) {
    // Cursor box disabled - not drawing the blue cursor
    // const pos = this.position;
    // ctx.fillRect(pos.x, pos.y, this.charWidth, this.charHeight);
    
    if (this.cursorTimeout) {
      window.clearTimeout(this.cursorTimeout);
    }
    if (this.raf) {
      window.cancelAnimationFrame(this.raf);
    }
    
    // this.opacity = 0.72;
    // this.cursorTimeout = window.setTimeout(() => this.fadeOut(ctx), 2200);
  }

  fadeOut(ctx: CanvasRenderingContext2D) {
    // Cursor box disabled - fade out not needed
    // const startTime = Date.now();
    // const animate = () => {
    //   const elapsed = Date.now() - startTime;
    //   const newOpacity = this.opacity - (0.1 * elapsed) / 300;
    //   
    //   if (newOpacity <= 0) {
    //     this.clear(ctx);
    //   } else {
    //     ctx.save();
    //     this.clear(ctx);
    //     this.opacity = newOpacity;
    //     ctx.globalAlpha = this.opacity;
    //     const pos = this.position;
    //     ctx.fillRect(pos.x, pos.y, this.charWidth, this.charHeight);
    //     ctx.restore();
    //     this.raf = requestAnimationFrame(animate);
    //   }
    // };
    // this.raf = requestAnimationFrame(animate);
  }

  nudge(ctx: CanvasRenderingContext2D, delta: Point) {
    this.update(ctx, this.position.add(delta));
  }

  moveleft(ctx: CanvasRenderingContext2D) {
    // If we have scrolled (canvas offset negative), unscroll the text
    if (this.typewriter.canvasOffset.x < 0) {
      this.typewriter.canvasOffset.x += this.charWidth;
      this.typewriter.redraw();
    } else if (this.position.x > 0) {
      // No offset - move cursor left normally (don't go off screen)
      this.nudge(ctx, new Point(-this.charWidth, 0));
    }
  }

  moveright(ctx: CanvasRenderingContext2D) {
    // For arrow key navigation: shift canvas left (simulate typing forward)
    this.typewriter.canvasOffset.x -= this.charWidth;
    this.typewriter.redraw();
  }

  moveup(ctx: CanvasRenderingContext2D) {
    this.nudge(ctx, new Point(0, -this.charHeight));
  }

  movedown(ctx: CanvasRenderingContext2D) {
    this.nudge(ctx, new Point(0, this.charHeight));
  }

  moveToClick(ctx: CanvasRenderingContext2D, clickPos: Point) {
    const newPos = clickPos.subtract(new Point(this.charWidth / 2, this.charHeight / 2));
    this.update(ctx, newPos);
  }

  reset(ctx: CanvasRenderingContext2D, startPos: Point) {
    this.position = startPos;
    this.updateInputPosition();
    // Cursor box disabled - not drawing
    // this.draw(ctx);
  }

  addtab(ctx: CanvasRenderingContext2D, tabWidth: number) {
    this.nudge(ctx, new Point(4 * tabWidth, 0));
  }

  newline(ctx: CanvasRenderingContext2D, startX: number, lineHeight: number) {
    // Move down one line, stay at center X
    this.update(ctx, new Point(startX, this.position.y + lineHeight));
    // Don't reset canvas offset - text continues scrolling
  }
}

// Typewriter class
class Typewriter {
  canvasOffset: Point;
  containerScale: number;
  chars: Char[];
  cursor: Cursor;
  textCanvas: HTMLCanvasElement;
  cursorCanvas: HTMLCanvasElement;
  textCtx: CanvasRenderingContext2D;
  cursorCtx: CanvasRenderingContext2D;
  inputElement: HTMLInputElement | null;
  fontSize: number;
  charWidth: number;
  charHeight: number;
  lineHeight: number;
  startPos: Point;
  viewportWidth: number;
  viewportHeight: number;

  constructor(
    textCanvas: HTMLCanvasElement,
    cursorCanvas: HTMLCanvasElement,
    inputElement: HTMLInputElement | null,
    fontSize: number,
    startYOverride?: number,
    viewportWidth: number = window.innerWidth,
    viewportHeight: number = window.innerHeight
  ) {
    this.textCanvas = textCanvas;
    this.cursorCanvas = cursorCanvas;
    const textCtx = textCanvas.getContext('2d');
    const cursorCtx = cursorCanvas.getContext('2d');
    if (!textCtx || !cursorCtx) {
      console.error('Failed to get canvas context');
      throw new Error('Canvas context not available');
    }
    this.textCtx = textCtx;
    this.cursorCtx = cursorCtx;
    this.inputElement = inputElement;
    this.fontSize = fontSize;
    this.charWidth = (12 * fontSize) / 20;
    this.charHeight = fontSize + 8;
    this.lineHeight = fontSize + 8;
    this.viewportWidth = viewportWidth;
    this.viewportHeight = viewportHeight;
    
    // Start at horizontal center, top of page
    const startX = this.viewportWidth / 2;
    const defaultStartY = Math.min(100, this.viewportHeight / 8);
    const startY = startYOverride ?? defaultStartY;
    this.startPos = new Point(startX, startY);
    
    this.canvasOffset = new Point(0, 0);
    this.containerScale = 1;
    this.chars = [];
    this.cursor = new Cursor(this, inputElement, this.charWidth, this.charHeight);
    
    this.resetCanvases();
  }

  resetCanvases(viewportWidth?: number, viewportHeight?: number) {
    if (typeof viewportWidth === 'number') {
      this.viewportWidth = viewportWidth;
    }
    if (typeof viewportHeight === 'number') {
      this.viewportHeight = viewportHeight;
    }

    const dpr = window.devicePixelRatio || 1;
    const width = this.viewportWidth;
    const height = this.viewportHeight;

    // Reset text canvas
    // NOTE: Setting canvas.width/height CLEARS the canvas!
    this.textCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.textCtx.canvas.width = width * dpr;
    this.textCtx.canvas.height = height * dpr;
    this.textCtx.canvas.style.width = `${width}px`;
    this.textCtx.canvas.style.height = `${height}px`;
    
    // Apply device pixel ratio scaling
    this.textCtx.scale(dpr, dpr);
    this.textCtx.globalAlpha = 1.0;
    
    // Clear canvas (transparent background to show dark gray page)
    this.textCtx.clearRect(0, 0, width, height);
    
    // Set up text rendering with fallback font
    this.textCtx.font = `${this.fontSize}px "Special Elite", "Courier New", monospace`;
    this.textCtx.textBaseline = 'top';
    this.textCtx.fillStyle = '#ffffff';
    
    // Ensure canvas is visible
    this.textCanvas.style.display = 'block';
    this.textCanvas.style.visibility = 'visible';
    
    // Canvas is ready
    
    // Reset cursor canvas
    this.cursorCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.cursorCtx.canvas.width = width * dpr;
    this.cursorCtx.canvas.height = height * dpr;
    this.cursorCtx.canvas.style.width = `${width}px`;
    this.cursorCtx.canvas.style.height = `${height}px`;
    this.cursorCtx.scale(dpr, dpr);
    this.cursorCtx.globalAlpha = 1.0;
    
    // Clear cursor canvas (transparent, since cursor rendering is disabled)
    this.cursorCtx.clearRect(0, 0, width, height);
    this.cursorCtx.scale(this.containerScale, this.containerScale);
  }

  addCharacter(char: string, x?: number, y?: number) {
    if (x !== undefined && y !== undefined) {
      this.chars.push(new Char(this, char, x, y));
      this.cursor.update(this.cursorCtx, new Point(x, y));
      this.redraw(); // Redraw immediately after adding character
      return;
    }

    for (let i = 0; i < char.length; i++) {
      const pos = this.cursor.position;
      
      // Add jitter to individual character position for realistic typewriter imperfection
      const jitterX = (Math.random() - 0.5) * 2.5; // Horizontal jitter (more visible)
      const jitterY = (Math.random() - 0.5) * 1.5; // Vertical jitter (more visible)
      const jitterRotation = (Math.random() - 0.5) * 0.08; // Tiny rotational jitter (in radians)
      
      // Store character in canvas space (subtract current offset to get canvas coordinates)
      // Add jitter to individual character positions
      const canvasX = pos.x - this.canvasOffset.x + jitterX;
      const canvasY = pos.y - this.canvasOffset.y + jitterY;
      const currentLineY = pos.y; // Track which line this character is on
      this.chars.push(new Char(this, char[i], canvasX, canvasY, jitterRotation, currentLineY));
      
      // After adding character, shift canvas left by block width + gap
      const gap = 2;
      const horizontalPadding = 6; // Match the padding in draw()
      const blockWidth = this.charWidth - gap + horizontalPadding;
      
      // Add spacing jitter so gaps between blocks vary slightly
      const spacingJitter = (Math.random() - 0.5) * 1.0;
      
      this.canvasOffset.x -= blockWidth + gap + spacingJitter;
    }
    // Redraw after adding all characters
    this.redraw();
  }

  redraw() {
    if (!this.textCtx) {
      console.error('Text context not available');
      return;
    }
    
    const dpr = window.devicePixelRatio || 1;
    const width = this.viewportWidth;
    // Use canvas's actual height (which may be taller than viewport for scrolling)
    const height = this.textCanvas.height / dpr;
    
    // Reset transform and apply device pixel ratio (same as resetCanvases)
    this.textCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.textCtx.scale(dpr, dpr);
    
    // Clear canvas (transparent to show dark gray page background)
    this.textCtx.clearRect(0, 0, width, height);
    
    // Draw light gray tape strips - one for each line
    if (this.chars.length > 0) {
      // Group characters by their line (lineY)
      const lineGroups = new Map<number, typeof this.chars>();
      this.chars.forEach((char) => {
        // Round lineY to avoid floating point issues
        const lineKey = Math.round(char.lineY);
        if (!lineGroups.has(lineKey)) {
          lineGroups.set(lineKey, []);
        }
        lineGroups.get(lineKey)!.push(char);
      });
      
      // Draw a separate tape strip for each line
      const gap = 2;
      const horizontalPadding = 6; // Match the padding in draw()
      const blockWidth = this.charWidth - gap + horizontalPadding;
      const padding = 4; // Small padding around each tape strip
      
      lineGroups.forEach((lineChars) => {
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        
        lineChars.forEach((char) => {
          // Use fixed offset for completed lines, current offset for active line
          const offsetX = char.fixedOffsetX !== null ? char.fixedOffsetX : this.canvasOffset.x;
          const x = char.x + offsetX;
          const y = char.y + this.canvasOffset.y;
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x + blockWidth);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y + this.charHeight);
        });
        
        // Add small padding
        minX = Math.max(0, minX - padding);
        maxX = Math.min(width, maxX + padding);
        minY = Math.max(0, minY - padding);
        maxY = Math.min(height, maxY + padding);
        
        // Draw gray tape strip for this line (darker gray like sidebar)
        this.textCtx.fillStyle = 'hsl(0 0% 14%)';
        this.textCtx.fillRect(minX, minY, maxX - minX, maxY - minY);
      });
    }
    
    // Set up for drawing characters (each char will draw its own black background)
    this.textCtx.globalAlpha = 1.0;
    this.textCtx.font = `${this.fontSize}px "Special Elite", "Courier New", monospace`;
    this.textCtx.textBaseline = 'top';
    
    // Draw all characters (each with black background + white text)
    this.chars.forEach((char) => {
      char.draw(this.textCtx);
    });
  }

  reset() {
    this.chars = [];
    this.canvasOffset = new Point(0, 0);
    this.containerScale = 1;
    this.resetCanvases();
    this.cursor.reset(this.cursorCtx, this.startPos);
    // Redraw will be called after resetCanvases
    setTimeout(() => this.redraw(), 0);
  }
}

export const TypewriterPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signInAnonymously } = useAuth();
  
  // Trigger anonymous sign-in if not authenticated
  useEffect(() => {
    if (!user && !authLoading) {
      console.log('🔐 TypewriterPage: No user - signing in anonymously...');
      signInAnonymously().catch(console.error);
    }
  }, [user, authLoading, signInAnonymously]);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const textCanvasRef = useRef<HTMLCanvasElement>(null);
  const cursorCanvasRef = useRef<HTMLCanvasElement>(null);
  const hammerCanvasRef = useRef<HTMLCanvasElement>(null);
  const snowCanvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typewriterRef = useRef<Typewriter | null>(null);
  const hasRedirectedRef = useRef(false); // Prevent double redirect
  
  // 📊 Session tracking for analytics
  const sessionStartTimeRef = useRef<number | null>(null);
  const userTypedTextRef = useRef<string>(''); // Text user types (not auto-typed)
  const completionTypeRef = useRef<'auto' | 'skipped'>('auto');
  
  const [started, setStarted] = useState(false);
  const [instructionText, setInstructionText] = useState('press return to enter');
  const [splashVisible, setSplashVisible] = useState(true);
  const [splashFading, setSplashFading] = useState(false);
  const pendingCharactersRef = useRef<string[]>([]);
  const [containerSize, setContainerSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [overlayShift, setOverlayShift] = useState(0);
  const [released, setReleased] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [fadePhase, setFadePhase] = useState<'idle' | 'fading' | 'complete'>('idle');
  
  // 📊 Save typewriter session to Firestore for analytics
  const saveTypewriterSession = async () => {
    try {
      const sessionData = {
        // User info
        userId: user?.uid || 'anonymous',
        
        // Session timing
        timestamp: serverTimestamp(),
        startedAt: sessionStartTimeRef.current ? new Date(sessionStartTimeRef.current).toISOString() : null,
        durationMs: sessionStartTimeRef.current ? Date.now() - sessionStartTimeRef.current : null,
        
        // User input
        userTypedText: userTypedTextRef.current,
        userTypedLength: userTypedTextRef.current.length,
        
        // Completion type
        completionType: completionTypeRef.current, // 'auto' or 'skipped'
        
        // Device/browser metadata
        deviceType: containerSize.width < 768 ? 'mobile' : 'desktop',
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        
        // Referrer & URL info
        referrer: document.referrer || null,
        pathname: window.location.pathname,
        
        // Touch/input capabilities
        touchSupported: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        maxTouchPoints: navigator.maxTouchPoints,
      };
      
      await addDoc(collection(db, 'system', 'typewriterSessions', 'sessions'), sessionData);
      console.log('📊 Typewriter session saved:', {
        userId: sessionData.userId?.substring(0, 8),
        durationMs: sessionData.durationMs,
        userTypedLength: sessionData.userTypedLength,
        completionType: sessionData.completionType,
      });
    } catch (error) {
      console.error('Failed to save typewriter session:', error);
      // Non-blocking - don't prevent redirect on failure
    }
  };

  // 🎬 Auto-redirect to chat when typewriter completes
  // Triggers on either:
  // 1. `released` = true (auto-typing finished)
  // 2. `fadePhase` = 'complete' (user clicked to skip/end early)
  useEffect(() => {
    const shouldRedirect = released || fadePhase === 'complete';
    
    if (shouldRedirect && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      
      // Determine completion type
      if (fadePhase === 'complete' && !released) {
        completionTypeRef.current = 'skipped';
      } else {
        completionTypeRef.current = 'auto';
      }
      
      console.log('🎬 Typewriter complete - saving session and redirecting...', {
        released,
        fadePhase,
        completionType: completionTypeRef.current,
      });
      
      // Save session data, mark user as onboarded, then redirect
      const completeOnboarding = async () => {
        // Save session analytics (non-blocking)
        await saveTypewriterSession();
        
        if (user?.uid) {
          try {
            await markSplashSeen(user.uid);
          } catch (error) {
            console.error('Failed to mark splash as seen:', error);
            // Continue with redirect even if marking fails
          }
        }
        
        // Small delay for visual completion, then redirect
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 500);
      };
      
      completeOnboarding();
    }
  }, [released, fadePhase, user, navigate, containerSize.width]);
  const [fadeFlashActive, setFadeFlashActive] = useState(false);
  const [remoteAutoScriptLines, setRemoteAutoScriptLines] = useState<string[] | null>(null);
  // Detect mobile (reactive to window size)
  const isMobile = containerSize.width < 768;
  // On mobile, use actual width; on desktop, use BASE_LAYOUT_WIDTH for consistent scaling
  const [layoutWidth, setLayoutWidth] = useState(() => {
    const mobile = window.innerWidth < 768;
    return mobile ? window.innerWidth : Math.max(window.innerWidth, BASE_LAYOUT_WIDTH);
  });
  // Scale factor: on mobile, no scaling needed (1:1); on desktop, scale down if needed
  const [scaleFactor, setScaleFactor] = useState(() => {
    const mobile = window.innerWidth < 768;
    return mobile ? 1 : Math.min(1, window.innerWidth / BASE_LAYOUT_WIDTH);
  });
  const [contentHeight, setContentHeight] = useState(window.innerHeight);
  const containerHeight = containerSize.height;
  const hammerHiddenRef = useRef(false);
  const hammerRevealTimeoutRef = useRef<number | null>(null);
  const hammerAnimationRef = useRef<{
    isAnimating: boolean;
    startTime: number;
    startPos: { x: number; y: number; z: number };
    targetPos: { x: number; y: number; z: number };
    batonLength: number; // Lock baton length at animation start
    duration: number;
    batonIndex: number; // Which baton (0-9) is animating
  } | null>(null);
  const fadeTimeoutRef = useRef<number | null>(null);
  const fadeFlashTimeoutRef = useRef<number | null>(null);
  const snowAnimationRef = useRef<{
    raf: number | null;
    startTime: number;
    particles: Array<{
      x: number;
      y: number;
      spawnTime: number;
      fadeDuration: number;
      radius: number;
      intensity: number;
    }>;
  } | null>(null);
  const rippleLayerRef = useRef<HTMLDivElement>(null);
  
  // Map each character to a specific baton (like a real typewriter) - 10 batons total
  const charToBatonMap = useRef<Map<string, number>>(new Map());
  const suppressNextKeyEventRef = useRef(false);
  const hasActivatedAudioRef = useRef(false);
  const touchUsedRef = useRef(false); // Track if touch was used to prevent click double-firing
  const manualTypingActiveRef = useRef(false); // Track whether user typing should show batons
  const manualCharQueueRef = useRef<string[]>([]);
  const manualQueueTimerRef = useRef<number | null>(null);
  const lastProcessedInputLengthRef = useRef<number>(1); // Track last processed input length (starts at 1 for '=')
  const splashFadeTimeoutRef = useRef<number | null>(null);
  const interruptionAudioCtxRef = useRef<AudioContext | null>(null);
  const lastSoundTypeRef = useRef<'keypress' | 'return' | null>(null);
  const [audioMuted, setAudioMuted] = useState(false);
  
  // Animation for centering completed tape on Enter
  const centeringAnimationRef = useRef<{
    isAnimating: boolean;
    startTime: number;
    startOffsetX: number;
    targetOffsetX: number;
    duration: number;
  } | null>(null);
  
  // Track completed lines - store their final centered X offset
  const completedLinesRef = useRef<Map<number, number>>(new Map()); // lineY -> finalOffsetX

  // Calculate font size based on window width - ensure readable on mobile
  const fontSize = isMobile 
    ? Math.max(14, Math.min(20, Math.floor(layoutWidth / 20))) // Mobile: 14-20px
    : Math.min(26, Math.floor(layoutWidth / 17)); // Desktop: up to 26px

  // Initialize audio
  const keypressHowl = useRef<Howl | null>(null);
  const returnHowl = useRef<Howl | null>(null);

  // Early audio activation - call on any interaction to wake up audio context
  const activateAudioEarly = useCallback(() => {
    if (!hasActivatedAudioRef.current && keypressHowl.current) {
      keypressHowl.current.play();
      keypressHowl.current.stop();
      hasActivatedAudioRef.current = true;
    }

    if (typeof window !== 'undefined') {
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextCtor) {
        if (!interruptionAudioCtxRef.current) {
          try {
            interruptionAudioCtxRef.current = new AudioContextCtor();
          } catch (error) {
            interruptionAudioCtxRef.current = null;
          }
        }
        if (interruptionAudioCtxRef.current?.state === 'suspended') {
          interruptionAudioCtxRef.current.resume().catch(() => undefined);
        }
      }
    }
  }, []);

  const playKeypressSound = useCallback(() => {
    if (audioMuted) return;
    // Ensure audio is activated
    if (!hasActivatedAudioRef.current) {
      activateAudioEarly();
    }
    keypressHowl.current?.stop();
    keypressHowl.current?.play();
    lastSoundTypeRef.current = 'keypress';
  }, [activateAudioEarly, audioMuted]);

  const playInterruptionChime = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!hasActivatedAudioRef.current) {
      activateAudioEarly();
    }

    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }

    if (!interruptionAudioCtxRef.current) {
      try {
        interruptionAudioCtxRef.current = new AudioContextCtor();
      } catch (error) {
        interruptionAudioCtxRef.current = null;
      }
    }

    const ctx = interruptionAudioCtxRef.current;
    if (!ctx) {
      return;
    }

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => undefined);
    }

    const now = ctx.currentTime + 0.001;
    const oscPrimary = ctx.createOscillator();
    const gainPrimary = ctx.createGain();
    gainPrimary.gain.setValueAtTime(0, now);
    gainPrimary.gain.linearRampToValueAtTime(0.32, now + 0.01);
    gainPrimary.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    oscPrimary.type = 'sine';
    oscPrimary.frequency.setValueAtTime(780, now);
    oscPrimary.frequency.exponentialRampToValueAtTime(460, now + 0.34);

    const oscSecondary = ctx.createOscillator();
    const gainSecondary = ctx.createGain();
    gainSecondary.gain.setValueAtTime(0, now + 0.015);
    gainSecondary.gain.linearRampToValueAtTime(0.18, now + 0.05);
    gainSecondary.gain.exponentialRampToValueAtTime(0.0001, now + 0.36);
    oscSecondary.type = 'triangle';
    oscSecondary.frequency.setValueAtTime(1040, now + 0.015);
    oscSecondary.frequency.exponentialRampToValueAtTime(520, now + 0.36);

    oscPrimary.connect(gainPrimary);
    gainPrimary.connect(ctx.destination);
    oscSecondary.connect(gainSecondary);
    gainSecondary.connect(ctx.destination);

    oscPrimary.start(now);
    oscSecondary.start(now + 0.015);
    oscPrimary.stop(now + 0.38);
    oscSecondary.stop(now + 0.4);
  }, [activateAudioEarly]);

  const spawnInterruptionRipple = useCallback((options?: { clientX?: number; clientY?: number; source?: 'pointer' | 'keyboard' }) => {
    if (!rippleLayerRef.current) {
      return;
    }

    const x = typeof options?.clientX === 'number' ? options!.clientX : window.innerWidth / 2;
    const y = typeof options?.clientY === 'number' ? options!.clientY : window.innerHeight / 2;
    const sourceClass = options?.source === 'keyboard' ? 'typewriter-ripple--keyboard' : 'typewriter-ripple--pointer';

    const ripple = document.createElement('span');
    ripple.className = `typewriter-ripple ${sourceClass}`.trim();
    ripple.style.setProperty('--ripple-x', `${x}px`);
    ripple.style.setProperty('--ripple-y', `${y}px`);

    rippleLayerRef.current.appendChild(ripple);

    requestAnimationFrame(() => {
      ripple.classList.add('typewriter-ripple--animate');
    });

    const handleAnimationEnd = () => {
      ripple.removeEventListener('animationend', handleAnimationEnd);
      ripple.removeEventListener('transitionend', handleAnimationEnd);
      ripple.remove();
    };

    ripple.addEventListener('animationend', handleAnimationEnd);
    ripple.addEventListener('transitionend', handleAnimationEnd);
  }, []);

  const autoTypingRef = useRef<{ triggered: boolean; active: boolean; timeouts: number[] }>({
    triggered: false,
    active: false,
    timeouts: [],
  });

  const clearAutoTypingTimers = useCallback(() => {
    autoTypingRef.current.timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    autoTypingRef.current.timeouts = [];
    autoTypingRef.current.active = false;
  }, []);

  const beginSplashFade = useCallback(() => {
    if (splashFading) {
      return;
    }

    setSplashFading(true);

    if (splashFadeTimeoutRef.current) {
      window.clearTimeout(splashFadeTimeoutRef.current);
    }

    splashFadeTimeoutRef.current = window.setTimeout(() => {
      setSplashVisible(false);
    }, SPLASH_FADE_DURATION);
  }, [splashFading]);

  const abortAutoTypingAndReturn = useCallback(() => {
    if (autoTypingRef.current.active) {
      clearAutoTypingTimers();
    }
  }, [clearAutoTypingTimers]);

  const stopSnowStatic = useCallback(() => {
    if (snowAnimationRef.current?.raf) {
      cancelAnimationFrame(snowAnimationRef.current.raf);
    }
    snowAnimationRef.current = null;
    if (snowCanvasRef.current) {
      const ctx = snowCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      }
    }
  }, []);

  const startSnowStatic = useCallback(() => {
    if (!snowCanvasRef.current) {
      return;
    }

    const canvas = snowCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = Math.max(window.innerHeight, document.body.clientHeight);

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    const targetCount = Math.min(
      SNOW_MAX_PARTICLES,
      Math.max(SNOW_MIN_PARTICLES, Math.floor((width * height) / SNOW_AREA_DIVISOR))
    );
    const spawnWindow = USER_INTERACTION_FADE_DURATION * 0.5;

    const particles = Array.from({ length: targetCount }, () => {
      const spawnProgress = Math.pow(Math.random(), 2.6);
      const spawnTime = spawnProgress * spawnWindow;
      const remaining = Math.max(0, USER_INTERACTION_FADE_DURATION - spawnTime);
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        spawnTime,
        fadeDuration: Math.max(300, remaining),
        radius: 0.6 + Math.random() * 1.1,
        intensity: 0.55 + Math.random() * 0.4,
      };
    });

    const animation = {
      raf: 0,
      startTime: performance.now(),
      particles,
    };

    const render = () => {
      if (!snowCanvasRef.current) {
        return;
      }

      const now = performance.now();
      const elapsed = now - animation.startTime;

      ctx.clearRect(0, 0, width, height);

      let hasVisibleParticles = false;

      for (const particle of animation.particles) {
        const relativeTime = elapsed - particle.spawnTime;
        if (relativeTime <= 0) {
          continue;
        }
        const progress = Math.min(relativeTime / particle.fadeDuration, 1);
        const alpha = Math.max(0, 1 - progress);
        if (alpha <= 0) {
          continue;
        }
        hasVisibleParticles = true;
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * particle.intensity})`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      if (elapsed < USER_INTERACTION_FADE_DURATION && hasVisibleParticles) {
        animation.raf = requestAnimationFrame(render);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
    };

    animation.raf = requestAnimationFrame(render);
    snowAnimationRef.current = animation;
  }, []);

  useEffect(() => {
    return () => {
      clearAutoTypingTimers();
    };
  }, [clearAutoTypingTimers]);

  useEffect(() => {
    let isCancelled = false;
    console.log('📜 Fetching typewriter script from Firestore...');
    TypewriterScriptService.getCurrentScript()
      .then((script) => {
        if (isCancelled) {
          return;
        }
        console.log('📜 Raw script received:', script);
        console.log('📜 Script details:', {
          length: script.length,
          preview: script.substring(0, 200),
          hasContent: script.length > 0,
        });
        const lines = parseScriptToLines(script);
        console.log('📜 Parsed lines:', lines);
        console.log('📜 Line count:', lines.length);
        if (lines.length) {
          setRemoteAutoScriptLines(lines);
        } else {
          console.warn('📜 No lines parsed, using default script');
          setRemoteAutoScriptLines([]);
        }
      })
      .catch((error) => {
        console.error('❌ Failed to load remote typewriter script:', error);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      if (fadeFlashTimeoutRef.current) {
        window.clearTimeout(fadeFlashTimeoutRef.current);
        fadeFlashTimeoutRef.current = null;
      }
      stopSnowStatic();
    };
  }, [stopSnowStatic]);

  useEffect(() => {
    return () => {
      if (manualQueueTimerRef.current) {
        cancelAnimationFrame(manualQueueTimerRef.current);
        manualQueueTimerRef.current = null;
      }
      manualCharQueueRef.current = [];
    };
  }, []);

  useEffect(() => {
    return () => {
      if (splashFadeTimeoutRef.current) {
        window.clearTimeout(splashFadeTimeoutRef.current);
        splashFadeTimeoutRef.current = null;
      }
    };
  }, []);

  // Project 3D point to 2D screen coordinates (top-down orthographic view)
  // We render as if looking straight down at the page: X/Y stay the same, Z only influences styling.
  const project3DTo2D = useCallback((x: number, y: number, z: number, width: number, height: number) => {
    return { x, y, scale: 1 };
  }, []);

  // Calculate 3D swing trajectory for a rigid baton of fixed length
  // The tip moves in 3D space: up/down (Z) and side-to-side (X/Y) while maintaining fixed length
  const calculateSwingTrajectory = useCallback((
    originPos: { x: number; y: number; z: number },
    targetPos: { x: number; y: number; z: number },
    progress: number, // 0 to 1
    batonLength: number
  ) => {
    // Direction from origin to target in the horizontal plane
    const dirX = targetPos.x - originPos.x;
    const dirY = targetPos.y - originPos.y;
    const horizontalDistance = Math.sqrt(dirX * dirX + dirY * dirY);
    const unitDirX = horizontalDistance > 0 ? dirX / horizontalDistance : 0;
    const unitDirY = horizontalDistance > 0 ? dirY / horizontalDistance : 0;

    // Define peak height (tip moves up toward viewer)
    // Ensure peak doesn't exceed baton length
    const desiredPeak = batonLength * 0.5; // Swing up to 50% of baton length
    const peakZ = Math.min(desiredPeak, batonLength * 0.9);

    // Smooth ease-in-out for realistic swing timing
    const easedProgress = progress < 0.5
      ? 2 * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;

    // Compute vertical motion (Z): tip moves up then down
    let currentZ: number;
    if (progress < 0.5) {
      // Going up (toward viewer)
      const upProgress = progress * 2; // 0 to 1 for first half
      currentZ = originPos.z + peakZ * (1 - Math.pow(1 - upProgress, 2));
    } else {
      // Coming down (back to page)
      const downProgress = (progress - 0.5) * 2; // 0 to 1 for second half
      currentZ = originPos.z + peakZ - peakZ * Math.pow(downProgress, 2);
    }

    // Calculate horizontal radius to maintain fixed baton length
    // batonLength^2 = horizontalRadius^2 + verticalOffset^2
    const verticalOffset = currentZ - originPos.z;
    const horizontalRadiusSquared = batonLength * batonLength - verticalOffset * verticalOffset;
    const horizontalRadius = Math.sqrt(Math.max(horizontalRadiusSquared, 0));

    // Base position along main direction (toward target)
    const baseX = originPos.x + unitDirX * horizontalRadius;
    const baseY = originPos.y + unitDirY * horizontalRadius;

    // Add lateral motion (side-to-side) perpendicular to main direction
    // This creates the side-to-side movement as seen from bird's eye view
    const perpX = -unitDirY; // Perpendicular X
    const perpY = unitDirX;   // Perpendicular Y
    
    // Lateral amplitude: stronger when going up, weaker when coming down
    const lateralAmplitude = Math.min(15, horizontalDistance * 0.2);
    let lateralFactor: number;
    if (progress < 0.5) {
      // Going up: lateral motion increases
      const upProgress = progress * 2;
      lateralFactor = Math.sin(upProgress * Math.PI) * lateralAmplitude;
    } else {
      // Coming down: lateral motion decreases
      const downProgress = (progress - 0.5) * 2;
      lateralFactor = Math.sin((1 - downProgress) * Math.PI) * lateralAmplitude;
    }

    // Apply lateral offset
    let tipX = baseX + perpX * lateralFactor;
    let tipY = baseY + perpY * lateralFactor;

    // CRITICAL: Re-project onto sphere to maintain exact fixed baton length
    // The tip must be exactly batonLength away from origin in 3D space
    const offsetX = tipX - originPos.x;
    const offsetY = tipY - originPos.y;
    const horizontalDist = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
    
    if (horizontalDist > 0 && horizontalRadius > 0) {
      // Scale horizontal position to maintain fixed horizontal radius
      const scale = horizontalRadius / horizontalDist;
      tipX = originPos.x + offsetX * scale;
      tipY = originPos.y + offsetY * scale;
    }

    return { x: tipX, y: tipY, z: currentZ };
  }, []);

  // Draw hammer line with 3D physics
  const drawHammer = useCallback(() => {
    if (!hammerCanvasRef.current) return;

    const ctx = hammerCanvasRef.current.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const typewriter = typewriterRef.current;
    const width = typewriter?.viewportWidth ?? window.innerWidth;
    // Calculate height from cursor position or use viewport height as minimum
    const cursorY = typewriter?.cursor.position.y ?? 0;
    const calcLineHeight = typewriter?.lineHeight ?? 20;
    const height = Math.max(window.innerHeight, cursorY + calcLineHeight * 2);

    // Set up canvas
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    hammerCanvasRef.current.width = width * dpr;
    hammerCanvasRef.current.height = height * dpr;
    hammerCanvasRef.current.style.width = `${width}px`;
    hammerCanvasRef.current.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Clear canvas (transparent, don't fill with black!)
    ctx.clearRect(0, 0, width, height);

    // Only show batons during auto-typing simulation or manual typing activity
    if (!typewriterRef.current || hammerHiddenRef.current || (!autoTypingRef.current.active && !manualTypingActiveRef.current && !released)) {
      return;
    }

    // Get cursor position (in canvas coordinates, including offset)
    const cursorPos = typewriterRef.current.cursor.position;
    const canvasOffset = typewriterRef.current.canvasOffset;
    
    const leftMargin = Math.min(100, width / 8);
    const rightMargin = width - leftMargin;
    const centerX = width / 2;
    const charHeight = typewriterRef.current.charHeight;
    const lineHeight = typewriterRef.current.lineHeight;
    
    // Position tips 1.5 lines below the text line (half line + one full line)
    const targetY = cursorPos.y + canvasOffset.y + lineHeight * 1.5;
    
    // Base positions should be half a page below the cursor vertically
    const baseY = cursorPos.y + canvasOffset.y + height / 2;
    
    // Create 10 batons arranged in an inverse fan
    const numBatons = 10;
    const batons: Array<{
      origin: { x: number; y: number; z: number };
      target: { x: number; y: number; z: number };
      length: number;
    }> = [];
    
    // Calculate uniform spacing for bases across the width
    const baseSpacing = (rightMargin - leftMargin) / (numBatons - 1);
    
    // Calculate uniform spacing for heads with small gaps
    const headGap = 4; // pixels between each head
    const totalHeadWidth = (numBatons - 1) * headGap;
    const headStartX = centerX - totalHeadWidth / 2;
    
    // Arc parameters - apply arc to HEAD positions to make vertical lines form convex arc
    const arcHeight = 7; // Arc height for head Y positions (pixels) - very subtle
    
    for (let i = 0; i < numBatons; i++) {
      // Base X: uniformly distributed from left to right margin
      const baseX = leftMargin + i * baseSpacing;
      
      // Head X: uniformly distributed with small gaps
      const headX = headStartX + i * headGap;
      
      // Head Y: arrange along a convex arc (parabola) - center lower, edges higher
      // This makes the vertical lines at the top form a visible convex arc
      const normalizedPos = (i / (numBatons - 1)) * 2 - 1; // -1 to 1
      const arcOffset = arcHeight * (1 - normalizedPos * normalizedPos); // Parabola
      const headY = targetY + arcOffset; // Add to make center lower (higher Y value)
      
      // Calculate the direction from head to base
      const dx = baseX - headX;
      const dy = baseY - headY;
      const dirLength = Math.sqrt(dx * dx + dy * dy);
      
      // Extend the baton so it goes way off screen
      // Calculate how far we need to extend to guarantee off-screen
      const extensionLength = Math.max(width * 2, height * 2);
      const extendedBaseX = headX + (dx / dirLength) * extensionLength;
      const extendedBaseY = headY + (dy / dirLength) * extensionLength;
      
      const origin = { x: extendedBaseX, y: extendedBaseY, z: 0 };
      const target = { x: headX, y: headY, z: 0 };
      const length = extensionLength; // Fixed very long length
      
      batons.push({ origin, target, length });
    }

    // Check if we're animating
    const anim = hammerAnimationRef.current;
    
    // Calculate strike position (at cursor center)
    const strikeX = centerX;
    const strikeY = cursorPos.y + canvasOffset.y + charHeight / 2;
    
    // Helper function to draw a single baton
    const drawSingleBaton = (
      batonIndex: number,
      restOrigin: { x: number; y: number; z: number },
      restTarget: { x: number; y: number; z: number },
      batonLength: number
    ) => {
      let currentOrigin = restOrigin;
      let currentTarget = restTarget;
      
      // If animating this baton, calculate animated positions
      if (anim && anim.isAnimating && anim.batonIndex === batonIndex) {
        const elapsed = Date.now() - anim.startTime;
        const progress = Math.min(elapsed / anim.duration, 1);
        
        if (progress >= 1) {
          // Animation complete
          anim.isAnimating = false;
        } else {
          // During animation: head moves to strike position, base adjusts to maintain length
          const easedProgress = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;
          
          // Interpolate head position from rest to strike position
          const headX = restTarget.x + (strikeX - restTarget.x) * easedProgress;
          const headY = restTarget.y + (strikeY - restTarget.y) * easedProgress;
          const headZ = 0;
          
          // Calculate where the base needs to be to maintain fixed baton length
          // Base moves toward center to allow head to reach strike point
          const dx = headX - restOrigin.x;
          const dy = headY - restOrigin.y;
          const currentDist = Math.sqrt(dx * dx + dy * dy);
          
          // If head-to-rest-origin distance exceeds baton length, move base toward head
          if (currentDist > batonLength) {
            const excessDist = currentDist - batonLength;
            const moveX = (dx / currentDist) * excessDist;
            const moveY = (dy / currentDist) * excessDist;
            currentOrigin = {
              x: restOrigin.x + moveX,
              y: restOrigin.y + moveY,
              z: 0
            };
          }
          
          currentTarget = { x: headX, y: headY, z: headZ };
        }
      }
      
      // Project 3D positions to 2D
      const origin2D = project3DTo2D(currentOrigin.x, currentOrigin.y, currentOrigin.z, width, height);
      const hammer2D = project3DTo2D(currentTarget.x, currentTarget.y, currentTarget.z, width, height);

      // Draw the baton line from origin to target (softer gray for less contrast)
      ctx.strokeStyle = '#b0b0b0'; // Disabled-level gray
      ctx.lineWidth = 2 * (1 + currentTarget.z / 200);
      ctx.beginPath();
      ctx.moveTo(origin2D.x, origin2D.y);
      ctx.lineTo(hammer2D.x, hammer2D.y);
      ctx.stroke();

      // Draw a small vertical line at the baton tip
      const lineLength = 24 * (1 + currentTarget.z / 200);
      ctx.strokeStyle = '#b0b0b0'; // Disabled-level gray
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(hammer2D.x, hammer2D.y - lineLength / 2);
      ctx.lineTo(hammer2D.x, hammer2D.y + lineLength / 2);
      ctx.stroke();
    };

    // Draw all 10 batons
    batons.forEach((baton, index) => {
      drawSingleBaton(index, baton.origin, baton.target, baton.length);
    });
  }, [project3DTo2D, calculateSwingTrajectory, released]);

  const recenterIfNeeded = useCallback(() => {
    if (!typewriterRef.current || released) return;

    const typewriter = typewriterRef.current;
    const viewportHeight = typewriter.viewportHeight;
    const cursorY = typewriter.cursor.position.y;
    const lowerThreshold = viewportHeight * 0.6;
    const targetY = viewportHeight * 0.4;

    if (cursorY <= lowerThreshold) {
      return;
    }

    const shift = cursorY - targetY;

    typewriter.cursor.position = new Point(typewriter.cursor.position.x, cursorY - shift);
    typewriter.cursor.updateInputPosition();
    typewriter.startPos = new Point(typewriter.startPos.x, typewriter.startPos.y - shift);
    typewriter.chars.forEach((char) => {
      char.y -= shift;
      char.lineY -= shift;
    });

    typewriter.redraw();
    drawHammer();

    setOverlayShift((prev) => prev + shift * scaleFactor);
    setShowWelcome(false);

    const newCursorY = typewriter.cursor.position.y;
    const minHeight = Math.max(window.innerHeight, newCursorY + typewriter.lineHeight * 2);
    setContentHeight(minHeight);
  }, [drawHammer, scaleFactor, released]);

  const applyCharacterNow = useCallback((char: string) => {
    if (!typewriterRef.current) return;
    typewriterRef.current.addCharacter(char);
    typewriterRef.current.redraw();
    const cursorY = typewriterRef.current.cursor.position.y;
    const lineHeight = typewriterRef.current.lineHeight;
    const minHeight = Math.max(window.innerHeight, cursorY + lineHeight * 2);
    setContentHeight(minHeight);
    recenterIfNeeded();
  }, [recenterIfNeeded]);

  const flushManualQueue = useCallback(() => {
    if (!manualCharQueueRef.current.length) {
      manualQueueTimerRef.current = null;
      return;
    }

    // Process up to 5 characters per frame for maximum responsiveness
    // Since we're now capturing ALL characters correctly, we can be more aggressive
    const charsToProcess = Math.min(5, manualCharQueueRef.current.length);
    for (let i = 0; i < charsToProcess; i++) {
      const nextChar = manualCharQueueRef.current.shift();
      if (nextChar) {
        applyCharacterNow(nextChar);
      }
    }

    // Continue processing on next frame if queue has more characters
    if (manualCharQueueRef.current.length > 0) {
      manualQueueTimerRef.current = requestAnimationFrame(() => {
        flushManualQueue();
      }) as unknown as number;
    } else {
      manualQueueTimerRef.current = null;
    }
  }, [applyCharacterNow]);

  const drainManualQueue = useCallback(() => {
    if (manualQueueTimerRef.current) {
      cancelAnimationFrame(manualQueueTimerRef.current);
      manualQueueTimerRef.current = null;
    }
    while (manualCharQueueRef.current.length) {
      const nextChar = manualCharQueueRef.current.shift();
      if (nextChar) {
        applyCharacterNow(nextChar);
      }
    }
  }, [applyCharacterNow]);

  const triggerGlobalFadeOut = useCallback(() => {
    if (!autoTypingRef.current.triggered) {
      return;
    }

    // Check if already fading/complete
    let alreadyFading = false;
    setFadePhase((currentPhase) => {
      if (currentPhase !== 'idle') {
        alreadyFading = true;
        return currentPhase;
      }
      // 🎬 Skip fade animation - go directly to complete for instant redirect
      return 'complete';
    });

    if (alreadyFading) {
      return;
    }

    // Cleanup
    autoTypingRef.current.active = false;
    manualTypingActiveRef.current = false;
    drainManualQueue();
    hammerHiddenRef.current = true;
    hammerAnimationRef.current = null;
    stopSnowStatic();

    if (inputRef.current) {
      inputRef.current.blur();
    }

    // Clear any pending fade timeouts
    if (fadeTimeoutRef.current) {
      window.clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
    if (fadeFlashTimeoutRef.current) {
      window.clearTimeout(fadeFlashTimeoutRef.current);
      fadeFlashTimeoutRef.current = null;
    }
  }, [drainManualQueue, stopSnowStatic]);

  useEffect(() => {
    const handleGlobalInteraction = (event: Event) => {
      if (!event.isTrusted) {
        return;
      }

      if (!autoTypingRef.current.triggered) {
        if (
          isMobile &&
          started &&
          !released &&
          inputRef.current &&
          (event.type === 'touchstart' || event.type === 'pointerdown')
        ) {
          requestAnimationFrame(() => {
            inputRef.current?.focus();
          });
        }
        return;
      }

      let clientX: number | undefined;
      let clientY: number | undefined;
      let source: 'pointer' | 'keyboard' | null = null;

      if (event.type === 'pointerdown') {
        const pointerEvent = event as PointerEvent;
        if (pointerEvent.pointerType === 'touch') {
          return; // touchstart will handle this interaction
        }
        clientX = pointerEvent.clientX;
        clientY = pointerEvent.clientY;
        source = 'pointer';
      } else if (event.type === 'touchstart') {
        const touchEvent = event as TouchEvent;
        const touch = touchEvent.changedTouches[0] ?? touchEvent.touches[0];
        if (!touch) {
          return;
        }
        clientX = touch.clientX;
        clientY = touch.clientY;
        source = 'pointer';
      } else if (event.type === 'keydown') {
        const keyEvent = event as KeyboardEvent;
        if (keyEvent.metaKey || keyEvent.ctrlKey || keyEvent.altKey) {
          return;
        }
        if (keyEvent.repeat) {
          return;
        }
        const isPrintable = keyEvent.key === 'Enter' || keyEvent.key === 'Backspace' || keyEvent.key.length === 1;
        if (!isPrintable) {
          return;
        }
        source = 'keyboard';
        clientX = window.innerWidth / 2;
        clientY = window.innerHeight / 2;
      }

      if (!source) {
        return;
      }

      if (autoTypingRef.current.active) {
        clearAutoTypingTimers();
      }

      playInterruptionChime();
      spawnInterruptionRipple({
        clientX,
        clientY,
        source: source === 'keyboard' ? 'keyboard' : 'pointer',
      });

      if (fadePhase !== 'idle') {
        return;
      }

      triggerGlobalFadeOut();
    };

    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];

    events.forEach((eventName) => {
      window.addEventListener(eventName, handleGlobalInteraction, true);
    });

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleGlobalInteraction, true);
      });
    };
  }, [triggerGlobalFadeOut, fadePhase, clearAutoTypingTimers, playInterruptionChime, spawnInterruptionRipple, isMobile, started, released]);

  // Update canvas heights when contentHeight changes
  useEffect(() => {
    if (textCanvasRef.current && cursorCanvasRef.current && hammerCanvasRef.current) {
      const dpr = window.devicePixelRatio || 1;
      // Update text canvas
      textCanvasRef.current.height = contentHeight * dpr;
      textCanvasRef.current.style.height = `${contentHeight}px`;
      // Update cursor canvas
      cursorCanvasRef.current.height = contentHeight * dpr;
      cursorCanvasRef.current.style.height = `${contentHeight}px`;
      // Update hammer canvas
      hammerCanvasRef.current.height = contentHeight * dpr;
      hammerCanvasRef.current.style.height = `${contentHeight}px`;
      
      // Redraw if typewriter exists
      if (typewriterRef.current) {
        typewriterRef.current.redraw();
        drawHammer();
      }
    }
  }, [contentHeight, drawHammer]);

  // Initialize typewriter
  useEffect(() => {
    if (textCanvasRef.current && cursorCanvasRef.current && inputRef.current && !typewriterRef.current) {
      const viewportHeight = Math.max(window.innerHeight, containerHeight);
      const baseTop = Math.min(100, viewportHeight / 8);
      const lineHeight = fontSize + 8;
      const startY = baseTop + lineHeight * 3;
      typewriterRef.current = new Typewriter(
        textCanvasRef.current,
        cursorCanvasRef.current,
        inputRef.current,
        fontSize,
        startY,
        layoutWidth,
        viewportHeight
      );
      // Force initial redraw to show test text
      if (typewriterRef.current) {
        typewriterRef.current.redraw();
      }
      // Draw initial hammer
      if (started) {
        drawHammer();
      }
    }
  }, [fontSize, started, drawHammer, layoutWidth, containerHeight]);

  // Handle resize - hide batons during resize and debounce
  useEffect(() => {
    let resizeTimeout: number | null = null;
    
    const handleResize = () => {
      // Immediately hide batons and clear canvas
      hammerHiddenRef.current = true;
      if (hammerCanvasRef.current) {
        const ctx = hammerCanvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, hammerCanvasRef.current.width, hammerCanvasRef.current.height);
        }
      }
      
      // Clear any pending reveal
      if (hammerRevealTimeoutRef.current !== null) {
        window.clearTimeout(hammerRevealTimeoutRef.current);
        hammerRevealTimeoutRef.current = null;
      }
      
      // Clear any pending resize processing
      if (resizeTimeout !== null) {
        window.clearTimeout(resizeTimeout);
      }
      
      // Debounce the actual resize processing
      resizeTimeout = window.setTimeout(() => {
        const mobile = window.innerWidth < 768;
        const viewportWidth = mobile ? window.innerWidth : Math.max(window.innerWidth, BASE_LAYOUT_WIDTH);
        const viewportHeight = window.innerHeight;
        const newScale = mobile ? 1 : Math.min(1, window.innerWidth / BASE_LAYOUT_WIDTH);

        setContainerSize({ width: window.innerWidth, height: viewportHeight });
        setLayoutWidth(viewportWidth);
        setScaleFactor(newScale);
        
      if (typewriterRef.current) {
          typewriterRef.current.resetCanvases(viewportWidth, viewportHeight);
          typewriterRef.current.viewportWidth = viewportWidth;
          typewriterRef.current.viewportHeight = viewportHeight;
          const newBaseTop = Math.min(100, viewportHeight / 8);
          const newFontSize = mobile
            ? Math.max(14, Math.min(20, Math.floor(viewportWidth / 20))) // Mobile: 14-20px
            : Math.min(26, Math.floor(viewportWidth / 17)); // Desktop: up to 26px
          const newLineHeight = newFontSize + 8;
          const newStartY = newBaseTop + newLineHeight * 3;
          typewriterRef.current.fontSize = newFontSize;
          typewriterRef.current.charWidth = (12 * newFontSize) / 20;
          typewriterRef.current.charHeight = newFontSize + 8;
          typewriterRef.current.lineHeight = newLineHeight;
          typewriterRef.current.startPos = new Point(viewportWidth / 2, newStartY);
          typewriterRef.current.cursor.charWidth = typewriterRef.current.charWidth;
          typewriterRef.current.cursor.charHeight = typewriterRef.current.charHeight;
          typewriterRef.current.cursor.reset(typewriterRef.current.cursorCtx, typewriterRef.current.startPos);
          typewriterRef.current.canvasOffset = new Point(0, 0);
        typewriterRef.current.redraw();
          // Update content height based on current cursor/content position
          const cursorY = typewriterRef.current.cursor.position.y;
          const minHeight = Math.max(window.innerHeight, cursorY + typewriterRef.current.lineHeight * 2);
          setContentHeight(minHeight);
      }
        
        // Wait a bit longer before revealing batons
        hammerRevealTimeoutRef.current = window.setTimeout(() => {
          hammerHiddenRef.current = false;
      drawHammer();
          hammerRevealTimeoutRef.current = null;
        }, 300);
      }, 150); // Debounce resize processing
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (hammerRevealTimeoutRef.current !== null) {
        window.clearTimeout(hammerRevealTimeoutRef.current);
        hammerRevealTimeoutRef.current = null;
      }
      if (resizeTimeout !== null) {
        window.clearTimeout(resizeTimeout);
      }
    };
  }, [drawHammer]);

  // Trigger hammer strike animation
  const triggerHammerStrike = useCallback((char: string) => {
    if (!typewriterRef.current) return;

    const typewriter = typewriterRef.current;
    const cursorPos = typewriter.cursor.position;
    const canvasOffset = typewriter.canvasOffset;
    const width = typewriter.viewportWidth;
    const height = typewriter.viewportHeight;
    const charHeight = typewriter.charHeight;
    const lineHeight = typewriter.lineHeight;
    
    // Check if this character already has a baton assignment (0-9)
    let batonIndex = charToBatonMap.current.get(char);
    if (batonIndex === undefined) {
      // First time typing this character - randomly assign it to one of 10 batons
      batonIndex = Math.floor(Math.random() * 10);
      charToBatonMap.current.set(char, batonIndex);
    }
    
    const leftMargin = Math.min(100, width / 8);
    const rightMargin = width - leftMargin;
    const centerX = width / 2;
    
    // Calculate the same baton positions as in drawHammer
    const numBatons = 10;
    const baseSpacing = (rightMargin - leftMargin) / (numBatons - 1);
    const headGap = 4;
    const totalHeadWidth = (numBatons - 1) * headGap;
    const headStartX = centerX - totalHeadWidth / 2;
    const arcHeight = 7; // Arc height for head Y positions to match drawHammer - very subtle
    
    // Calculate this specific baton's rest positions
    const baseX = leftMargin + batonIndex * baseSpacing;
    const baseY = cursorPos.y + canvasOffset.y + height / 2;
    
    const restHeadX = headStartX + batonIndex * headGap;
    const restTargetY = cursorPos.y + canvasOffset.y + lineHeight * 1.5;
    
    // Apply arc to head Y position - center lower, edges higher
    const normalizedPos = (batonIndex / (numBatons - 1)) * 2 - 1;
    const arcOffset = arcHeight * (1 - normalizedPos * normalizedPos);
    const restHeadY = restTargetY + arcOffset;
    
    // Calculate the direction from head to base and extend it off-screen
    const dx = baseX - restHeadX;
    const dy = baseY - restHeadY;
    const dirLength = Math.sqrt(dx * dx + dy * dy);
    
    // Extend the baton so it goes way off screen
    const extensionLength = Math.max(width * 2, height * 2);
    const extendedBaseX = restHeadX + (dx / dirLength) * extensionLength;
    const extendedBaseY = restHeadY + (dy / dirLength) * extensionLength;
    
    // Strike position (at cursor center)
    const strikeX = centerX;
    const strikeY = cursorPos.y + canvasOffset.y + charHeight / 2;
    
    // Use the extended baton length
    const batonLength = extensionLength;
    
    hammerAnimationRef.current = {
      isAnimating: true,
      startTime: Date.now(),
      startPos: { x: extendedBaseX, y: extendedBaseY, z: 0 },
      targetPos: { x: strikeX, y: strikeY, z: 0 },
      batonLength,
      duration: 60, // Maximum speed: 60ms (still visible, snappy, doesn't affect character/sound timing)
      batonIndex
    };
  }, []);

  const normalizeAfterRelease = useCallback(() => {
    if (!typewriterRef.current) return;

    const typewriter = typewriterRef.current;
    if (typewriter.chars.length === 0) return;

    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    typewriter.chars.forEach((char) => {
      minY = Math.min(minY, char.y);
      maxY = Math.max(maxY, char.y + typewriter.lineHeight);
    });

    const baseTop = Math.min(100, typewriter.viewportHeight / 8);
    const adjust = baseTop - minY;

    if (adjust !== 0) {
      typewriter.chars.forEach((char) => {
        char.y += adjust;
        char.lineY += adjust;
      });
      typewriter.cursor.position = new Point(typewriter.cursor.position.x, typewriter.cursor.position.y + adjust);
      typewriter.cursor.updateInputPosition();
      typewriter.startPos = new Point(typewriter.startPos.x, typewriter.startPos.y + adjust);
    }

    const contentBottom = maxY + adjust + typewriter.lineHeight * 2;
    const minHeight = Math.max(window.innerHeight, contentBottom);
    setContentHeight(minHeight);
    setOverlayShift(0);
    setShowWelcome(false);
    typewriter.redraw();
    drawHammer();
  }, [drawHammer]);

  const queueCharacter = useCallback((char: string) => {
    triggerHammerStrike(char);

    // Keep batons visible throughout manual typing session
    if (!autoTypingRef.current.active) {
      manualTypingActiveRef.current = true;
      // 📊 Track user-typed characters (not auto-typed)
      userTypedTextRef.current += char;
    }

    if (!autoTypingRef.current.active) {
      playKeypressSound();
      // Add character to queue
      manualCharQueueRef.current.push(char);
      
      // Start processing immediately if not already processing
      if (!manualQueueTimerRef.current) {
        // Process first character immediately for instant feedback
        const nextChar = manualCharQueueRef.current.shift();
        if (nextChar) {
          applyCharacterNow(nextChar);
        }
        // Continue processing queue with requestAnimationFrame for smooth, fast processing
        if (manualCharQueueRef.current.length > 0) {
          manualQueueTimerRef.current = requestAnimationFrame(() => {
            flushManualQueue();
          }) as unknown as number;
        }
      }
    } else {
      const delay = 200;
      window.setTimeout(() => applyCharacterNow(char), delay);
    }
  }, [triggerHammerStrike, applyCharacterNow, flushManualQueue, playKeypressSound]);

  const runCarriageReturn = useCallback((options?: { manual?: boolean; suppressSound?: boolean }) => {
    if (!typewriterRef.current) return;

    if (options?.manual) {
      drainManualQueue();
      // 📊 Track manual newlines
      userTypedTextRef.current += '\n';
    }

    const typewriter = typewriterRef.current;
    // Canvas is scaled, so center is at layoutWidth/2 (which visually appears centered)
    const canvasCenter = layoutWidth / 2;

    if (typewriter.chars.length > 0) {
      const gap = 2;
      const horizontalPadding = 6;
      const blockWidth = typewriter.charWidth - gap + horizontalPadding;
      const currentLineY = typewriter.cursor.position.y;
      const currentLineChars = typewriter.chars.filter(
        (char) => Math.abs(char.lineY - currentLineY) < typewriter.lineHeight / 2
      );

      if (currentLineChars.length > 0) {
        let minX = Infinity;
        let maxX = -Infinity;

        currentLineChars.forEach((char) => {
          const x = char.x + typewriter.canvasOffset.x;
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x + blockWidth);
        });

        const tapeWidth = maxX - minX;
        const tapeCenter = minX + tapeWidth / 2;
        // Center tape at canvas center (which appears centered after scaling)
        const offsetNeeded = canvasCenter - tapeCenter;

        centeringAnimationRef.current = {
          isAnimating: true,
          startTime: Date.now(),
          startOffsetX: typewriter.canvasOffset.x,
          targetOffsetX: typewriter.canvasOffset.x + offsetNeeded,
          duration: 300,
        };
      }
    }

    const extraSpacing = typewriter.lineHeight * 1.4;
    typewriter.cursor.newline(typewriter.cursorCtx, typewriter.startPos.x, extraSpacing);
    
    // Update content height after newline
    const cursorY = typewriter.cursor.position.y;
    const minHeight = Math.max(window.innerHeight, cursorY + typewriter.lineHeight * 2);
    setContentHeight(minHeight);
    recenterIfNeeded();

    window.setTimeout(() => {
      if (!typewriterRef.current || !centeringAnimationRef.current) {
        return;
      }
      const finalOffsetX = centeringAnimationRef.current.targetOffsetX;
      const completedLineY = typewriterRef.current.cursor.position.y - extraSpacing;
      typewriterRef.current.chars.forEach((char) => {
        if (Math.abs(char.lineY - completedLineY) < typewriterRef.current!.lineHeight / 2) {
          char.fixedOffsetX = finalOffsetX;
        }
      });
      typewriterRef.current.canvasOffset.x = 0;
      // Update content height again after centering
      const updatedCursorY = typewriterRef.current.cursor.position.y;
      const updatedMinHeight = Math.max(window.innerHeight, updatedCursorY + typewriterRef.current.lineHeight * 2);
      setContentHeight(updatedMinHeight);
      recenterIfNeeded();
    }, 300);

    if (!options?.suppressSound && !audioMuted) {
      // Only play if last sound wasn't also a return
      if (lastSoundTypeRef.current !== 'return') {
        returnHowl.current?.play();
      }
      lastSoundTypeRef.current = 'return';
    }
    drawHammer();
    if (options?.manual) {
      manualTypingActiveRef.current = false;
      if (isMobile && inputRef.current) {
        inputRef.current.blur();
      }
    }
  }, [drawHammer, layoutWidth, recenterIfNeeded, drainManualQueue, audioMuted]);

  const startAutomatedTyping = useCallback(() => {
    if (!typewriterRef.current) return;
    clearAutoTypingTimers();
    autoTypingRef.current.active = true;
    manualTypingActiveRef.current = true;
    setReleased(false);
    setShowWelcome(false);

    const lines =
      remoteAutoScriptLines && remoteAutoScriptLines.length > 0
        ? remoteAutoScriptLines
        : DEFAULT_AUTO_SCRIPT_LINES;

    const baseMinDelay = 18;
    const baseMaxDelay = 30;
    const newlinePause = 260;
    const recenterPadding = 280;

    let accumulatedDelay = 200;
    let charactersTyped = 0;

    const effectiveWidth = layoutWidth * scaleFactor;
    const shouldChunkLines = effectiveWidth < 960 || isMobile;

    lines.forEach((line, lineIndex) => {
      const isLastLine = lineIndex === lines.length - 1;
      if (line.length > 0) {
        if (shouldChunkLines) {
          // Split line into words (preserving punctuation)
          const words = line.split(/\s+/).filter((word: string) => word.length > 0);

          // Estimate character width for fitting calculations
          const estimatedCharWidth = typewriterRef.current
            ? typewriterRef.current.charWidth - 2 + 6 // blockWidth approximation
            : 20; // fallback estimate
          const estimatedSpaceWidth = estimatedCharWidth * 0.6;
          const availableWidth = effectiveWidth * 0.85; // keep margins in mind

          // Calculate approximate width of the full line
          const approximateLineWidth = words.reduce((sum: number, word: string, index: number) => {
            const wordWidth = word.length * estimatedCharWidth;
            const spaceWidth = index === 0 ? 0 : estimatedSpaceWidth;
            return sum + wordWidth + spaceWidth;
          }, 0);

          let wordGroups: string[][] = [];

          if (approximateLineWidth <= availableWidth) {
            wordGroups = [words];
          } else {
            let currentGroup: string[] = [];
            let currentGroupWidth = 0;

            words.forEach((word: string) => {
              const baseWordWidth = word.length * estimatedCharWidth;
              const wordWidth = Math.max(baseWordWidth, estimatedCharWidth); // ensure at least one char width

              if (currentGroup.length === 0) {
                currentGroup.push(word);
                currentGroupWidth = wordWidth;
                return;
              }

              const projectedWidth = currentGroupWidth + estimatedSpaceWidth + wordWidth;

              if (projectedWidth > availableWidth) {
                wordGroups.push([...currentGroup]);
                currentGroup = [word];
                currentGroupWidth = wordWidth;
              } else {
                currentGroup.push(word);
                currentGroupWidth = projectedWidth;
              }
            });

            if (currentGroup.length > 0) {
              wordGroups.push(currentGroup);
            }
          }

          wordGroups.forEach((group: string[], groupIndex: number) => {
            const isLastGroup = groupIndex === wordGroups.length - 1;

            group.forEach((word: string, wordIndex: number) => {
              if (wordIndex > 0) {
                const spaceJitter = baseMinDelay + Math.random() * Math.max(8, baseMaxDelay - baseMinDelay);
                accumulatedDelay += spaceJitter;
                const spaceTimeoutId = window.setTimeout(() => {
                  if (!typewriterRef.current) return;
                  queueCharacter(' ');
                }, accumulatedDelay);
                autoTypingRef.current.timeouts.push(spaceTimeoutId);
              }

              word.split('').forEach((char: string) => {
                const jitter = baseMinDelay + Math.random() * Math.max(8, baseMaxDelay - baseMinDelay);
                accumulatedDelay += jitter;
                const skipSound = charactersTyped % 3 !== 0;
                const timeoutId = window.setTimeout(() => {
                  if (!typewriterRef.current) return;
                  if (!skipSound) {
                    playKeypressSound();
                  }
                  queueCharacter(char);
                  if (inputRef.current) {
                    inputRef.current.value = '=';
                  }
                }, accumulatedDelay);
                autoTypingRef.current.timeouts.push(timeoutId);
                charactersTyped += 1;
              });
            });

            if (!(isLastGroup && isLastLine)) {
              const newlineDelay = accumulatedDelay + newlinePause;
              const newlineTimeout = window.setTimeout(() => {
                if (!typewriterRef.current) return;
                runCarriageReturn();
              }, newlineDelay);
              autoTypingRef.current.timeouts.push(newlineTimeout);
              accumulatedDelay = newlineDelay + recenterPadding;
            }
          });
        } else {
          line.split('').forEach((char: string) => {
            const jitter = baseMinDelay + Math.random() * Math.max(8, baseMaxDelay - baseMinDelay);
            accumulatedDelay += jitter;
            const skipSound = charactersTyped % 3 !== 0;
            const timeoutId = window.setTimeout(() => {
              if (!typewriterRef.current) return;
              if (!skipSound) {
                playKeypressSound();
              }
              queueCharacter(char);
              if (inputRef.current) {
                inputRef.current.value = '=';
              }
            }, accumulatedDelay);
            autoTypingRef.current.timeouts.push(timeoutId);
            charactersTyped += 1;
          });

          if (!isLastLine) {
            const newlineDelay = accumulatedDelay + newlinePause;
            const newlineTimeout = window.setTimeout(() => {
              if (!typewriterRef.current) return;
              runCarriageReturn();
            }, newlineDelay);
            autoTypingRef.current.timeouts.push(newlineTimeout);
            accumulatedDelay = newlineDelay + recenterPadding;
          }
        }
      } else {
        const firstNewlineDelay = accumulatedDelay + newlinePause;
        let soundPlayed = false;
        const firstNewlineTimeout = window.setTimeout(() => {
          if (!typewriterRef.current) return;
          runCarriageReturn();
          soundPlayed = true;
        }, firstNewlineDelay);
        autoTypingRef.current.timeouts.push(firstNewlineTimeout);

        const secondNewlineDelay = firstNewlineDelay + recenterPadding + newlinePause;
        const secondNewlineTimeout = window.setTimeout(() => {
          if (!typewriterRef.current) return;
          runCarriageReturn({ suppressSound: soundPlayed });
        }, secondNewlineDelay);
        autoTypingRef.current.timeouts.push(secondNewlineTimeout);
        accumulatedDelay = secondNewlineDelay + recenterPadding;
      }
    });

    const finalNewlineDelay = accumulatedDelay + newlinePause;
    const finalNewlineTimeout = window.setTimeout(() => {
      if (!typewriterRef.current) return;
      runCarriageReturn();
    }, finalNewlineDelay);
    autoTypingRef.current.timeouts.push(finalNewlineTimeout);
    accumulatedDelay = finalNewlineDelay + recenterPadding;

    const finalizeTimeout = window.setTimeout(() => {
      autoTypingRef.current.active = false;
      normalizeAfterRelease();
      setReleased(true);
    }, accumulatedDelay + recenterPadding + FINAL_RELEASE_HOLD);
    autoTypingRef.current.timeouts.push(finalizeTimeout);
  }, [
    clearAutoTypingTimers,
    playKeypressSound,
    queueCharacter,
    runCarriageReturn,
    layoutWidth,
    scaleFactor,
    isMobile,
    remoteAutoScriptLines,
  ]);

  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.platform);
    keypressHowl.current = new Howl({
      src: [keypressSound],
      pool: isIOS ? 3 : 7,
    });
    returnHowl.current = new Howl({
      src: [returnSound],
      pool: 2,
    });
  }, []);

  // Update hammer and centering animation continuously when started
  useEffect(() => {
    if (!started || !typewriterRef.current) return;

    let rafId: number;
    const updateAnimations = () => {
      // Handle centering animation
      const centerAnim = centeringAnimationRef.current;
      if (centerAnim && centerAnim.isAnimating && typewriterRef.current) {
        const elapsed = Date.now() - centerAnim.startTime;
        const progress = Math.min(elapsed / centerAnim.duration, 1);
        
        // Ease-out animation for smooth deceleration
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        
        // Update horizontal offset to center the tape
        typewriterRef.current.canvasOffset.x = 
          centerAnim.startOffsetX + 
          (centerAnim.targetOffsetX - centerAnim.startOffsetX) * easedProgress;
        
        if (progress >= 1) {
          centerAnim.isAnimating = false;
        }
        
        // Redraw with new offset
        typewriterRef.current.redraw();
      }
      
      drawHammer();
      rafId = requestAnimationFrame(updateAnimations);
    };
    rafId = requestAnimationFrame(updateAnimations);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [started, drawHammer]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!typewriterRef.current) return;

    if (suppressNextKeyEventRef.current) {
      e.preventDefault();
      return;
    }

    const isModifier = e.altKey || e.ctrlKey || e.metaKey;
    const specialKeys: { [key: string]: string } = {
      Tab: 'TAB',
      Enter: 'ENTER',
      Backspace: 'BACKSPACE',
      ArrowLeft: 'LEFT',
      ArrowUp: 'UP',
      ArrowRight: 'RIGHT',
      ArrowDown: 'DOWN',
    };

    const specialKey = specialKeys[e.key];
    if (specialKey && !isModifier) {
      if (specialKey === 'TAB') {
        setTimeout(() => inputRef.current?.focus(), 10);
        e.preventDefault();
        return;
      }
    }

    if (!isModifier && e.key.length === 1) {
      playKeypressSound();
    }
  }, [playKeypressSound]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const isModifier = e.altKey || e.ctrlKey || e.metaKey;
    if (e.key === 'Tab' || e.key === 'Enter' || isModifier) {
      e.preventDefault();
    }
  }, []);

  const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!typewriterRef.current) return;
    
    if (suppressNextKeyEventRef.current) {
      e.preventDefault();
      suppressNextKeyEventRef.current = false;
      if (inputRef.current) {
        inputRef.current.value = '=';
        requestAnimationFrame(() => inputRef.current?.focus());
      }
      return;
    }
    
    const target = e.currentTarget;
    if (!target) return;

    const isModifier = e.altKey || e.ctrlKey || e.metaKey;
    if (isModifier) {
      return;
    }

    const navButtons: { [key: string]: () => void } = {
      Backspace: () => {
        if (typewriterRef.current!.chars.length > 0) {
          typewriterRef.current!.chars.pop();
          if (typewriterRef.current!.canvasOffset.x < 0) {
            typewriterRef.current!.canvasOffset.x += typewriterRef.current!.charWidth;
          } else if (typewriterRef.current!.cursor.position.x > 0) {
            typewriterRef.current!.cursor.nudge(
              typewriterRef.current!.cursorCtx, 
              new Point(-typewriterRef.current!.charWidth, 0)
            );
          }
          typewriterRef.current!.redraw();
          drawHammer();
        }
      },
      Tab: () => {
        typewriterRef.current!.cursor.addtab(typewriterRef.current!.cursorCtx, typewriterRef.current!.charWidth);
        drawHammer();
      },
      ArrowLeft: () => {
        typewriterRef.current!.cursor.moveleft(typewriterRef.current!.cursorCtx);
        drawHammer();
      },
      ArrowUp: () => {
        typewriterRef.current!.cursor.moveup(typewriterRef.current!.cursorCtx);
        drawHammer();
      },
      ArrowRight: () => {
        typewriterRef.current!.cursor.moveright(typewriterRef.current!.cursorCtx);
        drawHammer();
      },
      ArrowDown: () => {
        typewriterRef.current!.cursor.movedown(typewriterRef.current!.cursorCtx);
        drawHammer();
      },
      Enter: () => {
        if (!typewriterRef.current) return;
        
        let shouldTriggerAutoTyping = false;
        if (!autoTypingRef.current.triggered) {
          autoTypingRef.current.triggered = true;
          shouldTriggerAutoTyping = true;
        }

        runCarriageReturn({ manual: true });

        if (shouldTriggerAutoTyping) {
          setInstructionText('touch, click, or type anywhere to start chatting');
          const delay = 520;
          const timeoutId = window.setTimeout(() => {
            if (!typewriterRef.current) return;
            startAutomatedTyping();
          }, delay);
          autoTypingRef.current.timeouts.push(timeoutId);
        }
      },
    };

    const navAction = navButtons[e.key];
    if (navAction) {
      navAction();
      // Reset tracking after navigation actions
      target.value = '=';
      lastProcessedInputLengthRef.current = 1;
    } else {
      // Process ALL new characters that have accumulated in the input buffer
      // This is the key: the browser's native input element never drops characters
      const inputValue = target.value;
      const currentLength = inputValue.length;
      
      // Extract all new characters since last processing
      // The input always starts with '=' (length 1), so new chars start at index 1
      if (currentLength > lastProcessedInputLengthRef.current) {
        const newChars = inputValue.slice(lastProcessedInputLengthRef.current);
        
        // Queue each new character individually
        for (let i = 0; i < newChars.length; i++) {
          queueCharacter(newChars[i]);
            }
        
        // Update tracking to current length
        lastProcessedInputLengthRef.current = currentLength;
      }
    }

    // Reset input to placeholder after processing
    // Use requestAnimationFrame to ensure smooth processing
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.value = '=';
        lastProcessedInputLengthRef.current = 1;
        inputRef.current.focus();
      }
    });
  }, [drawHammer, queueCharacter, startAutomatedTyping, runCarriageReturn]);

  const handleFocus = useCallback(() => {
    if (inputRef.current) {
      if (!inputRef.current.value || inputRef.current.value.length === 0) {
        inputRef.current.value = '='; // Ensure placeholder is set
        lastProcessedInputLengthRef.current = 1;
      }
      inputRef.current.focus();
    }
  }, []);

  // Backup mechanism: process input changes immediately
  // This catches characters even if keyup events are delayed
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    if (!target || !typewriterRef.current) return;
    
    const inputValue = target.value;
    const currentLength = inputValue.length;
    
    // Process any new characters that weren't caught by keyup yet
    if (currentLength > lastProcessedInputLengthRef.current) {
      const newChars = inputValue.slice(lastProcessedInputLengthRef.current);
      
      // Queue each new character
      for (let i = 0; i < newChars.length; i++) {
        queueCharacter(newChars[i]);
      }
      
      // Update tracking
      lastProcessedInputLengthRef.current = currentLength;
    }
  }, [queueCharacter]);

  useEffect(() => {
    if (!started) {
      const timeout = setTimeout(() => {
        const splash = document.querySelector<HTMLDivElement>('.typewriter-splash');
        splash?.focus();
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [started]);

  useEffect(() => {
    const activeClass = 'typewriter-page-active';
    document.documentElement.classList.add(activeClass);
    document.body.classList.add(activeClass);
    if (started) {
      document.body.classList.add('started');
    } else {
      document.body.classList.remove('started');
    }
    return () => {
      document.documentElement.classList.remove(activeClass);
      document.body.classList.remove(activeClass);
      document.body.classList.remove('started');
    };
  }, [started]);

  const startTypewriter = useCallback((options?: { seed?: string | string[]; suppressNextKey?: boolean }) => {
    if (started) {
      if (autoTypingRef.current.active) {
        abortAutoTypingAndReturn();
      }
      return;
    }
 
    if (fadeTimeoutRef.current) {
      window.clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
    setFadePhase('idle');
    stopSnowStatic();
    
    beginSplashFade();
    setStarted(true);
    sessionStartTimeRef.current = Date.now(); // 📊 Track session start
    setOverlayShift(0);
    setReleased(false);
    setShowWelcome(true);
    setFadeFlashActive(false);
    if (fadeFlashTimeoutRef.current) {
      window.clearTimeout(fadeFlashTimeoutRef.current);
      fadeFlashTimeoutRef.current = null;
    }

    // Activate audio early on any interaction
    activateAudioEarly();
    
    const seedInput = options?.seed;
    const seedCharacters = seedInput
      ? typeof seedInput === 'string'
        ? Array.from(seedInput)
        : seedInput.flatMap((chunk) => Array.from(chunk))
      : [];
 
    if (seedCharacters.length) {
      pendingCharactersRef.current = seedCharacters;
      suppressNextKeyEventRef.current = options?.suppressNextKey ?? false;
    } else if (options?.suppressNextKey) {
      suppressNextKeyEventRef.current = true;
    }
    
    // Use setTimeout for mobile keyboard compatibility - allows keyboard to appear
    setTimeout(() => {
      const focusInput = () => {
      if (inputRef.current) {
          inputRef.current.value = '=';
          // Use click() method for better mobile keyboard support
        inputRef.current.focus();
          // Also trigger click to ensure keyboard appears on mobile
          if (isMobile) {
            inputRef.current.click();
      }
        }
      };
      focusInput();

      if (pendingCharactersRef.current.length) {
        const processPending = () => {
          if (!typewriterRef.current) {
            setTimeout(processPending, 50);
            return;
          }
          const pendingChars = [...pendingCharactersRef.current];
          pendingCharactersRef.current = [];
          if (pendingChars.length) {
            pendingChars.forEach((pendingChar, index) => {
              // Slight stagger for readability when seeding multiple characters
              if (index === 0) {
            queueCharacter(pendingChar);
              } else {
                setTimeout(() => {
                  queueCharacter(pendingChar);
                }, index * MANUAL_BUFFER_INTERVAL);
              }
            });
          }
          suppressNextKeyEventRef.current = false;
          if (inputRef.current) {
            inputRef.current.value = '=';
            setTimeout(() => {
              inputRef.current?.focus();
              if (isMobile) {
                inputRef.current?.click();
              }
            }, 50);
          }
        };
        setTimeout(processPending, 100);
      }
    }, isMobile ? 100 : 0); // Slight delay on mobile to allow keyboard to appear
  }, [started, activateAudioEarly, playKeypressSound, queueCharacter, abortAutoTypingAndReturn, isMobile, beginSplashFade, stopSnowStatic]);

  const handleSplashKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const isPrintable = event.key.length === 1;
      if (isPrintable || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        beginSplashFade();
        // Pass first character directly to startTypewriter for reliable handling
        startTypewriter({
          seed: isPrintable ? event.key : undefined,
          suppressNextKey: isPrintable,
        });
      }
    },
    [beginSplashFade, startTypewriter]
  );

  const handleSplashTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      // Don't preventDefault - allow normal touch behavior for mobile keyboard
      touchUsedRef.current = true;
      // Activate audio early on touch
      activateAudioEarly();
      beginSplashFade();
      startTypewriter({
        seed: '> ',
        suppressNextKey: false,
      });
      // Reset touch flag after a delay to allow click on non-touch devices
      setTimeout(() => {
        touchUsedRef.current = false;
      }, 300);
    },
    [startTypewriter, activateAudioEarly, beginSplashFade]
  );

  const handleSplashClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // Prevent click from firing if touch was just used (prevents double-firing on touch devices)
      if (touchUsedRef.current) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      // Activate audio early on click
      activateAudioEarly();
      beginSplashFade();
      startTypewriter({
        seed: '> ',
      });
    },
    [startTypewriter, activateAudioEarly, beginSplashFade]
  );

  const overlayTop = Math.min(100, containerSize.height / 8);
  const adjustedOverlayTop = Math.max(0, overlayTop - overlayShift);

  const fadeActive = fadePhase !== 'idle';
  const fadeComplete = fadePhase === 'complete';
  const pageFadeStyle = {
    '--typewriter-fade-duration': `${USER_INTERACTION_FADE_DURATION}ms`,
  } as React.CSSProperties;
  const pageClassName = `typewriter-page${fadeComplete ? ' typewriter-page--fade-complete' : ''}`;
  const contentClassName = `typewriter-page__content${fadeActive ? ' typewriter-page__content--fading' : ''}`;
  const overlayClassName = [
    'typewriter-fade-overlay',
    fadeActive ? 'typewriter-fade-overlay--active' : '',
    fadeFlashActive ? 'typewriter-fade-overlay--flash' : '',
  ]
    .filter(Boolean)
    .join(' ');
  
  const flashClassName = [
    'typewriter-flash',
    fadeFlashActive ? 'typewriter-flash--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const containerStyle = released
    ? {
        position: 'relative' as const,
        transform: `scale(${scaleFactor})`,
        transformOrigin: 'top center',
        width: `${layoutWidth}px`,
        maxWidth: '100%',
        margin: '0 auto',
        minHeight: `${contentHeight}px`,
      }
    : {
        position: 'absolute' as const,
        left: '50%',
        transform: `translateX(-50%) scale(${scaleFactor})`,
        transformOrigin: 'top center',
        width: `${layoutWidth}px`,
        minHeight: `${contentHeight}px`,
      };

  const SpeakerIcon: React.FC<{ muted: boolean }> = ({ muted }) => (
    <svg
      className="typewriter-mute-svg"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <path
        d="M3.5 6.2H5.7L8.8 3.5v9L5.7 9.8H3.5V6.2Z"
        fill="currentColor"
        fillOpacity="0.7"
      />
      {!muted && (
        <path
          d="M11 5.2c1 .9 1.6 2.1 1.6 3.3s-.6 2.4-1.6 3.3"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeOpacity="0.7"
          fill="none"
        />
      )}
      {muted && (
        <path
          d="M10.8 4.8l3.4 6.4"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeOpacity="0.8"
        />
      )}
    </svg>
  );

  return (
    <div className={pageClassName} ref={containerRef} style={pageFadeStyle}>
      <div className={contentClassName}>
      {splashVisible && (
        <div
          className={`typewriter-splash${splashFading ? ' typewriter-splash--fading' : ''}`}
          onKeyDown={handleSplashKeyDown}
          onTouchStart={handleSplashTouchStart}
          onClick={handleSplashClick}
          tabIndex={0}
          style={{
            transform: `scale(${scaleFactor})`,
            transformOrigin: 'top center',
          }}
        >
          <h1>
            welcome to{' '}
            <span className="doorway-welcome-logo">
              <span className="doorway-ionoff">
                <span className="doorway-ionoff-block doorway-ionoff-block--i">i</span>
                <span className="doorway-ionoff-block doorway-ionoff-block--on">on</span>
                <span className="doorway-ionoff-block doorway-ionoff-block--off">off</span>
              </span>
            </span>
          </h1>
          <h2 className="doorway-cta doorway-cta--initial">type something to get started...</h2>
        </div>
      )}

      {showWelcome && (
        <div
          className="doorway-welcome-line"
          style={{
            top: `${adjustedOverlayTop}px`,
            transform: `translateX(-50%) scale(${scaleFactor})`,
            transformOrigin: 'top center',
          }}
        >
          <span className="doorway-welcome-prefix">welcome to</span>
          <span className="doorway-welcome-logo">
            <span className="doorway-ionoff">
              <span className="doorway-ionoff-block doorway-ionoff-block--i">i</span>
              <span className="doorway-ionoff-block doorway-ionoff-block--on">on</span>
              <span className="doorway-ionoff-block doorway-ionoff-block--off">off</span>
            </span>
          </span>
        </div>
      )}

      {/* Instruction line - moves from below to above welcome line */}
      <div
        className="doorway-instruction-line"
        style={{
          top: `${adjustedOverlayTop}px`,
          transform: `translate(-50%, ${started ? '-54px' : '68px'}) scale(${scaleFactor})`,
          transformOrigin: 'top center',
        }}
      >
        {instructionText}
        <button
          className="typewriter-mute-button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setAudioMuted((prev) => !prev);
            window.requestAnimationFrame(() => {
              if (started && inputRef.current) {
                inputRef.current.focus();
                if (isMobile) {
                  inputRef.current.click();
                }
              }
            });
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          aria-label={audioMuted ? 'Unmute audio' : 'Mute audio'}
          title={audioMuted ? 'Unmute audio' : 'Mute audio'}
          data-muted={audioMuted ? 'true' : 'false'}
        >
          <SpeakerIcon muted={audioMuted} />
        </button>
      </div>

      <div 
        id="container" 
        className="typewriter-container"
        onTouchStart={(e) => {
          // On mobile, focus input when container is touched to bring up keyboard
          if (isMobile && inputRef.current && started) {
            e.preventDefault();
            inputRef.current.focus();
          }
        }}
        style={containerStyle}
      >
        <canvas
          ref={textCanvasRef}
          id="text-canvas"
          width={layoutWidth}
          height={contentHeight}
        />
        <canvas
          ref={cursorCanvasRef}
          id="cursor-canvas"
          width={layoutWidth}
          height={contentHeight}
        />
        <canvas
          ref={hammerCanvasRef}
          id="hammer-canvas"
          width={layoutWidth}
          height={contentHeight}
          className="hammer-canvas"
        />
        <input
          ref={inputRef}
          type="text"
          id="text-input"
          tabIndex={0}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-lpignore="true"
          defaultValue="="
            onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
          onKeyUp={handleKeyUp}
          onFocus={handleFocus}
          onTouchStart={(e) => {
            // Ensure input is focused when touched on mobile
            e.currentTarget.focus();
          }}
        />
      </div>
      <div
        className="typewriter-height-spacer"
        aria-hidden
        style={{ height: `${contentHeight * scaleFactor}px` }}
      />
      </div>
      <div className={flashClassName} />
      <div ref={rippleLayerRef} className="typewriter-ripple-layer" />
      <div className={overlayClassName}>
        <canvas ref={snowCanvasRef} className="typewriter-fade-overlay__snow" />
      </div>
    </div>
  );
};
